import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as css from './agreements.module.scss';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import OpenInNewTab from '../../components/link/openInNewTab';
import translations from './agreementErrors.i18n';
import Button from '../../components/button';
import PanelHeader from '../../components/panelheader/panelHeader';
import {
  PraksisavtalestedValideringfeilDto,
  PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum,
  PraksisavtalestedValideringfeiltypeDto,
  PraksisstedMedValideringsfeilDto,
} from '../../api/generated/studiested-generated-api';
import Icon, { IconType } from '../../components/icon';
import { useLoader } from '../../api/useLoader';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { listIsDefinedAndHasElements } from '../../util/listutils';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import { useActivityContextUHA } from '../activitycontexts';
import {
  CustomizableDialogButtonGroup,
  CustomizableDialogContainer,
  CustomizableDialogContent,
  CustomizableDialogFooter,
  CustomizableDialogHeader,
  CustomizeableDialogIcon,
  CustomizeableDialogSubtitle,
  CustomizeableDialogTitle,
  CustomizeableDialogTitles,
  CustomizeableDialogTitlesAndIcon,
} from '../../components/dialog';
import Spinner from '../../components/spinner/spinner';
import {
  OnStedHoverCard,
  StedType,
} from '../../components/hierarchy/onhoverhierarchyinfo';
import { NavLink } from 'react-router-dom';
import { isNullOrUndefined } from '../../util/isNullOrUndefined';
import { useFormatMessageFor } from '../../i18n/translation';

const Headers = {
  Praksissted: 'Praksissted',
  Avtale: 'Avtale',
  Feil: 'Feil',
  Handling: 'Handlinger',
} as const;

enum ModalBtnAction {
  CANCEL,
  CONFIRM,
  EMPTY_LIST,
}

const useDeleteBestillingBesvarelserForTaggedErrors = (): ((
  errorsToDelete: PraksisstedMedValideringsfeilDto[],
) => void) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { formatMessage } = useIntl();
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const {
    praksisaktivitet: { id },
    reloadPraksisaktivitet,
  } = useActivityContextUHA();
  const errorCallback = useAddErrorNotificationCallback();

  const labels = useMemo(
    () => ({
      deleteSuccess: formatMessage({
        id: translations.deleteSuccess.id,
      }),
    }),
    [formatMessage],
  );

  const handleDeleteBesvarelse = (besvarelseId: string): Promise<void> =>
    praksiskalenderApi.praksisaktivitetIdErrorBesvarelseBidDelete({
      pathParams: { id, bid: besvarelseId },
    });

  const handleDeleteBestilling = (bestillingId: string): Promise<void> =>
    praksiskalenderApi.praksisaktivitetIdErrorBestillingBidDelete({
      pathParams: { id, bid: bestillingId },
    });

  const toDeleteBesvarelseOrBestillingPromise = (
    feil: PraksisstedMedValideringsfeilDto | undefined,
  ): Promise<void> => {
    switch (feil?.valideringsfeil?.sourceType) {
      case PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Besvarelse:
        return handleDeleteBesvarelse(feil.valideringsfeil.id);
      case PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Bestilling:
        return handleDeleteBestilling(feil.valideringsfeil.id);
      default:
        return Promise.resolve();
    }
  };

  return (errorsToDelete: PraksisstedMedValideringsfeilDto[]) =>
    Promise.all(errorsToDelete.map(toDeleteBesvarelseOrBestillingPromise))
      .then(() => {
        addSuccessNotification(labels.deleteSuccess);
        reloadPraksisaktivitet();
      })
      .catch(errorCallback);
};

const AgreementErrorsWrapper: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const {
    praksisaktivitet: { id },
  } = useActivityContextUHA();

  const [
    praksisaktivitetErrorsForDeletion,
    setPraksisaktivitetErrorsForDeletion,
  ] = useState<PraksisstedMedValideringsfeilDto[]>([]);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const deleteBestillingBesvarelserForTaggedErrors =
    useDeleteBestillingBesvarelserForTaggedErrors();

  const { data: praksisaktivitetErrors, loading } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdErrorGet({
        pathParams: { id },
      }),
    [id],
  );

  const labels = useFormatMessageFor(translations);

  const sourceTypesToLabel = useMemo(
    () => ({
      [PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Besvarelse]:
        labels.besvarelse,
      [PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Bestilling]:
        labels.bestilling,
    }),
    [labels],
  );

  if (!listIsDefinedAndHasElements(praksisaktivitetErrors)) return <></>;

  const putIntoListForDeletion = (
    valideringsfeil: PraksisstedMedValideringsfeilDto | undefined,
  ) => {
    if (valideringsfeil) {
      setPraksisaktivitetErrorsForDeletion([
        ...praksisaktivitetErrorsForDeletion,
        valideringsfeil,
      ]);
    }
  };

  const onModalClose = (action: ModalBtnAction) => {
    switch (action) {
      case ModalBtnAction.EMPTY_LIST:
        setPraksisaktivitetErrorsForDeletion([]);
        break;
      case ModalBtnAction.CONFIRM:
        deleteBestillingBesvarelserForTaggedErrors(
          praksisaktivitetErrorsForDeletion,
        );
        break;
    }

    setModalIsOpen(false);
  };

  return (
    <PanelHeader
      title={labels.panelHeaderFeil}
      subtitle={labels.panelHeaderFeilSubtitle}
      collapsible
    >
      {loading ? (
        <Spinner height={'5em'} />
      ) : (
        <AgreementErrors
          putIntoListForDeletion={putIntoListForDeletion}
          praksisaktivitetErrors={praksisaktivitetErrors}
          praksisaktivitetErrorsForDeletion={praksisaktivitetErrorsForDeletion}
          sourceTypesToLabel={sourceTypesToLabel}
        />
      )}
      <div className={css.errorConfirmBtnContainer}>
        <Button.Secondary
          onClick={() => setModalIsOpen(true)}
          disabled={praksisaktivitetErrorsForDeletion.length === 0}
          data-cy="delete-action-btn"
        >
          {labels.saveDelete}
        </Button.Secondary>
      </div>
      {modalIsOpen && (
        <AgreementErrorsConfirmationModal
          praksisaktivitetErrorsForDeletion={praksisaktivitetErrorsForDeletion}
          sourceTypesToLabel={sourceTypesToLabel}
          onClose={onModalClose}
        />
      )}
    </PanelHeader>
  );
};

const filterErrorForDeletion = (
  error: PraksisstedMedValideringsfeilDto,
  praksisaktivitetErrorsForDeletion: PraksisstedMedValideringsfeilDto[],
) =>
  !praksisaktivitetErrorsForDeletion.find(
    (errorForDeletion) =>
      error.valideringsfeil?.id === errorForDeletion.valideringsfeil?.id &&
      error.valideringsfeil?.sourceType ===
        errorForDeletion.valideringsfeil?.sourceType,
  );

interface AgreementErrorsProps {
  praksisaktivitetErrors: PraksisstedMedValideringsfeilDto[];
  praksisaktivitetErrorsForDeletion: PraksisstedMedValideringsfeilDto[];
  putIntoListForDeletion: (
    valideringsfeil: PraksisstedMedValideringsfeilDto | undefined,
  ) => void;
  sourceTypesToLabel: SourceTypesToString;
}

const AgreementErrors: React.FC<
  React.PropsWithChildren<AgreementErrorsProps>
> = ({
  praksisaktivitetErrors,
  praksisaktivitetErrorsForDeletion,
  putIntoListForDeletion,
  sourceTypesToLabel,
}) => {
  const { formatMessage } = useIntl();
  const headerLabels = useMemo(
    () => ({
      [Headers.Praksissted]: formatMessage({
        id: translations.headerPraksissted.id,
      }),
      [Headers.Avtale]: formatMessage({ id: translations.headerAvtale.id }),
      [Headers.Feil]: formatMessage({ id: translations.headerDescription.id }),
      [Headers.Handling]: formatMessage({
        id: translations.headerHandlinger.id,
      }),
    }),
    [formatMessage],
  );

  const tableHeaders = useMemo(
    () =>
      Object.entries(headerLabels).map(([type, label]) => (
        <TableCellTh
          key={`${type}-${label}`}
          align={type === Headers.Handling ? 'right' : 'inherit'}
        >
          {label}
        </TableCellTh>
      )),
    [headerLabels],
  );

  const errorTableRows = useMemo(
    () =>
      praksisaktivitetErrors
        .filter((error) =>
          filterErrorForDeletion(error, praksisaktivitetErrorsForDeletion),
        )
        .map((feil) => {
          const { id, avtaleNavn, avtaleId, valideringsfeil } = feil;
          const uniqueId = `${id}-${avtaleId}-${valideringsfeil?.id}`;

          return (
            <TableRow
              id={`agreementErrorsRow-${uniqueId}`}
              rowBackground={'alert'}
              key={id}
            >
              <PracticePlaceCell
                feil={feil}
                uniqueId={uniqueId}
                headerLabel={headerLabels[Headers.Praksissted]}
              />
              <AgreementCell
                avtaleId={avtaleId}
                avtalenavn={avtaleNavn}
                headerLabel={headerLabels[Headers.Avtale]}
              />
              <StatusCell
                headerLabel={headerLabels[Headers.Feil]}
                feil={feil}
              />
              <ActionCell
                headerLabel={headerLabels[Headers.Handling]}
                uniqueId={uniqueId}
                feil={feil}
                sourceTypesToLabel={sourceTypesToLabel}
                putIntoListForDeletion={putIntoListForDeletion}
              />
            </TableRow>
          );
        }),
    [
      praksisaktivitetErrors,
      praksisaktivitetErrorsForDeletion,
      headerLabels,
      putIntoListForDeletion,
      sourceTypesToLabel,
    ],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{tableHeaders}</TableRow>
      </TableHeader>
      <TableMain>{errorTableRows}</TableMain>
    </Table>
  );
};

interface PracticePlaceNameWithHoverCardProps {
  practicePlaceId: string;
  practicePlaceName: string;
  renderPrefix?: () => JSX.Element;
}

const PracticePlaceNameWithHoverCard: React.FC<
  React.PropsWithChildren<PracticePlaceNameWithHoverCardProps>
> = ({ practicePlaceId, practicePlaceName, renderPrefix }) => (
  <div className={css.errorPracticePlace}>
    <div className={css.prefixAndPracticeplace}>
      {renderPrefix && renderPrefix()}
      <div className={css.practiceplaceName}>
        <NavLink to={`/practiceplaces/profile/${practicePlaceId}`}>
          {practicePlaceName}
        </NavLink>
      </div>
    </div>
    <OnStedHoverCard
      sted={StedType.PRAKSISSTED}
      id={practicePlaceId}
      navn={practicePlaceId}
    />
  </div>
);

interface CellProps {
  headerLabel: string;
}

interface PracticePlaceCellProps extends CellProps {
  feil: PraksisstedMedValideringsfeilDto;
  uniqueId: string;
}

const PracticePlaceCell: React.FC<
  React.PropsWithChildren<PracticePlaceCellProps>
> = ({ feil, uniqueId, headerLabel }) => {
  const { id, navn, valideringsfeil } = feil;
  const { id: bestillingsstedId, navn: bestillingsstedNavn } = useMemo(() => {
    const defaultValue = { navn: '', id: '' };

    if (
      isNullOrUndefined(valideringsfeil) ||
      !valideringsfeil.praksisstedForBesvarelsensBestilling
    )
      return defaultValue;

    return valideringsfeil.praksisstedForBesvarelsensBestilling;
  }, [valideringsfeil]);

  const labels = useFormatMessageFor(translations);

  const getSourceBestillingKapasitetLabel = useCallback(
    (
      source: PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum | undefined,
    ): string => {
      if (!source) return '';

      return source ===
        PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Bestilling
        ? `${labels.numBestilling} ${
            feil?.valideringsfeil?.bestilling || labels.none
          }`
        : `${labels.numKapasitet} ${
            feil?.valideringsfeil?.kapasitet || labels.none
          }`;
    },
    [labels, feil],
  );

  return (
    <TableCellTd
      className={css.errorPracticePlaceCell}
      id={`agreementErrorsRow-${uniqueId}-navn`}
      header={headerLabel}
    >
      <div className={css.errorPracticePlaceWrapper}>
        <PracticePlaceNameWithHoverCard
          practicePlaceId={id}
          practicePlaceName={navn}
        />
        {bestillingsstedId && (
          <PracticePlaceNameWithHoverCard
            practicePlaceId={bestillingsstedId}
            practicePlaceName={bestillingsstedNavn}
            renderPrefix={() => (
              <div className={css.errorPracticePlaceBestilling}>
                {labels.bestillingAt}
              </div>
            )}
          />
        )}
        <div className={css.kapasitetBestilling}>
          {getSourceBestillingKapasitetLabel(feil?.valideringsfeil?.sourceType)}
        </div>
      </div>
    </TableCellTd>
  );
};

interface AgreementCellProps extends CellProps {
  avtaleId: string | undefined;
  avtalenavn: string | undefined;
}

const AgreementCell: React.FC<React.PropsWithChildren<AgreementCellProps>> = ({
  headerLabel,
  avtaleId,
  avtalenavn,
}) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      showAvtale: formatMessage({ id: translations.showAvtale.id }),
    }),
    [formatMessage],
  );

  return (
    <TableCellTd header={headerLabel} className={css.errorAgreementCell}>
      <OpenInNewTab
        to={`/practiceplaces/practiceplaceagreement/${avtaleId}`}
        label={labels.showAvtale}
        title={avtalenavn}
      />
      {avtalenavn}
    </TableCellTd>
  );
};

interface StatusCellProps extends CellProps {
  feil: PraksisstedMedValideringsfeilDto;
}

const StatusCell: React.FC<React.PropsWithChildren<StatusCellProps>> = ({
  headerLabel,
  feil,
}) => {
  const labels = useFormatMessageFor(translations);

  const getSourceLabelPart = useCallback(
    (
      source: PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum | undefined,
    ): string => {
      switch (source) {
        case PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Besvarelse:
          return labels.sourceTypeBesvarelse;
        case PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum.Bestilling:
          return labels.sourceTypeBestilling;
        default:
          return '';
      }
    },
    [labels],
  );

  const getSourceLabel = useCallback(
    (
      source: PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum | undefined,
    ): string =>
      `${getSourceLabelPart(source)} ${labels.warningOutsideAgreement}`,
    [labels, getSourceLabelPart],
  );

  const getErrorDetails = useCallback(
    (feiltype: PraksisavtalestedValideringfeilDto) => {
      const sourceLabel = getSourceLabelPart(feiltype.sourceType);
      const combineLabels = (label: string): string =>
        `${sourceLabel} ${label}`;

      switch (feiltype.errorType) {
        case PraksisavtalestedValideringfeiltypeDto.MissingAvtalested:
          return combineLabels(labels.errorTypeMissingAvtalested);
        case PraksisavtalestedValideringfeiltypeDto.MissingFromAktivitetskrav:
          return combineLabels(labels.errorTypeMissingFromAktivitetskrav);
        case PraksisavtalestedValideringfeiltypeDto.MissingFromTopTree:
          return combineLabels(labels.errorTypeMissingFromTopTree);
        case PraksisavtalestedValideringfeiltypeDto.MissingOmfattetPraksissted:
          return combineLabels(labels.errorTypeMissingOmfattetPraksissted);
        case PraksisavtalestedValideringfeiltypeDto.DeaktivertSted:
          return combineLabels(labels.errorTypeDeaktivertSted);
        case PraksisavtalestedValideringfeiltypeDto.PathMismatch:
          return combineLabels(labels.errorTypePathMismatch);
        case PraksisavtalestedValideringfeiltypeDto.UtloeptSted:
          return combineLabels(labels.errorTypeUtloeptSted);
        case PraksisavtalestedValideringfeiltypeDto.UnderliggendeFeil:
          return combineLabels(labels.errorTypeUnderliggendeFeil);
        case PraksisavtalestedValideringfeiltypeDto.HierarchicalBestilling:
          return combineLabels(labels.errorTypeHierarkiskBestilling);
        case PraksisavtalestedValideringfeiltypeDto.OverlappingAvtalestedHierarchies:
          return combineLabels(
            labels.errorTypeOverlappendeAvtalestedHierarkier,
          );
        case PraksisavtalestedValideringfeiltypeDto.OverlappingBesvarelse:
          return combineLabels(labels.errorTypeOverlappingBesvarelse);
        case PraksisavtalestedValideringfeiltypeDto.NotWithinAvtalePeriod:
          return combineLabels(labels.errorTypeNotWithinAvtalePeriod);
        case PraksisavtalestedValideringfeiltypeDto.InactivePraksistype:
          return combineLabels(labels.errorTypeInactivePraksistype);
        case PraksisavtalestedValideringfeiltypeDto.InactivePraksisavtale:
          return combineLabels(labels.errorTypeInactivePraksisavtale);
      }
    },
    [labels, getSourceLabelPart],
  );

  return (
    <TableCellTd header={headerLabel} className={css.errorStatusCell}>
      {feil.valideringsfeil && (
        <div
          className={css.errorStatusCellWrapper}
          title={getSourceLabel(feil.valideringsfeil.sourceType)}
        >
          <Icon type={IconType.Warning} />
          {getErrorDetails(feil.valideringsfeil)}
        </div>
      )}
    </TableCellTd>
  );
};

interface ActionCellProps extends CellProps {
  uniqueId: string;
  feil: PraksisstedMedValideringsfeilDto;
  sourceTypesToLabel: SourceTypesToString;
  putIntoListForDeletion: (
    valideringsfeil: PraksisstedMedValideringsfeilDto | undefined,
  ) => void;
}

const ActionCell: React.FC<React.PropsWithChildren<ActionCellProps>> = ({
  headerLabel,
  uniqueId,
  feil,
  sourceTypesToLabel,
  putIntoListForDeletion,
}) => {
  const { valideringsfeil } = feil;

  const { formatMessage } = useIntl();
  const btnLabel = useMemo(
    () =>
      `${formatMessage({ id: translations.actionSlett.id })} ${
        valideringsfeil?.sourceType
          ? sourceTypesToLabel[valideringsfeil.sourceType]
          : ''
      }`,
    [formatMessage, valideringsfeil, sourceTypesToLabel],
  );

  return (
    <TableCellTd
      header={headerLabel}
      className={css.errorActionCell}
      align={'right'}
    >
      <Button.Secondary
        id={`agreementErrorsRow-${uniqueId}-actionBtn`}
        onClick={() => putIntoListForDeletion(feil)}
      >
        {btnLabel}
      </Button.Secondary>
    </TableCellTd>
  );
};

type SourceTypesToString = {
  [key in PraksisavtalestedValideringfeilDtoSourceTypeDtoEnum]: string;
};

interface AgreementErrorsConfirmationModalProps {
  onClose: (action: ModalBtnAction) => void;
  praksisaktivitetErrorsForDeletion: PraksisstedMedValideringsfeilDto[];
  sourceTypesToLabel: SourceTypesToString;
}

const AgreementErrorsConfirmationModal: React.FC<
  React.PropsWithChildren<AgreementErrorsConfirmationModalProps>
> = ({ onClose, praksisaktivitetErrorsForDeletion, sourceTypesToLabel }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      errorModalTitle: formatMessage({ id: translations.errorModalTitle.id }),
      errorModalSubtitle: formatMessage({
        id: translations.errorModalSubtitle.id,
      }),
      cancel: formatMessage({ id: translations.cancel.id }),
      errorModalEmptyTagged: formatMessage({
        id: translations.errorModalEmptyTagged.id,
      }),
      errorModalConfirmDelete: formatMessage({
        id: translations.errorModalConfirmDelete.id,
      }),
      errorModalContentHeader: formatMessage({
        id: translations.errorModalContentHeader.id,
      }),
      unknown: formatMessage({ id: translations.unknown.id }),
    }),
    [formatMessage],
  );

  const handleCancel = () => onClose(ModalBtnAction.CANCEL);
  const handleEmpty = () => onClose(ModalBtnAction.EMPTY_LIST);
  const handleConfirmDelete = () => onClose(ModalBtnAction.CONFIRM);

  const renderedErrorsForDeletion = useMemo(
    () =>
      praksisaktivitetErrorsForDeletion.map((error) => (
        <div key={error.id} className={css.errorForDeletion}>
          <strong>{error.navn}</strong>
          <span className={css.errorForDeletionType}>
            {error.valideringsfeil?.sourceType
              ? sourceTypesToLabel[error.valideringsfeil.sourceType]
              : labels.unknown}
          </span>
        </div>
      )),
    [praksisaktivitetErrorsForDeletion, sourceTypesToLabel, labels],
  );

  return (
    <CustomizableDialogContainer onCancel={handleCancel} isOpen>
      <CustomizableDialogHeader>
        <CustomizeableDialogTitlesAndIcon>
          <CustomizeableDialogIcon type={IconType.Info} />
          <CustomizeableDialogTitles>
            <CustomizeableDialogTitle>
              {labels.errorModalTitle}
            </CustomizeableDialogTitle>
            <CustomizeableDialogSubtitle>
              {labels.errorModalSubtitle}
            </CustomizeableDialogSubtitle>
          </CustomizeableDialogTitles>
        </CustomizeableDialogTitlesAndIcon>
      </CustomizableDialogHeader>
      <CustomizableDialogContent>
        <h3 className={css.modalContentHeader}>
          {labels.errorModalContentHeader}
        </h3>
        {renderedErrorsForDeletion}
      </CustomizableDialogContent>
      <CustomizableDialogFooter>
        <Button.Quaternary onClick={handleCancel} data-cy="modal-cancel-action">
          {labels.cancel}
        </Button.Quaternary>
        <CustomizableDialogButtonGroup>
          <Button.Tertiary onClick={handleEmpty} data-cy="modal-empty-action">
            {labels.errorModalEmptyTagged}
          </Button.Tertiary>
          <Button.Secondary
            onClick={handleConfirmDelete}
            data-cy="modal-confirm-delete-action"
          >
            {labels.errorModalConfirmDelete}
          </Button.Secondary>
        </CustomizableDialogButtonGroup>
      </CustomizableDialogFooter>
    </CustomizableDialogContainer>
  );
};

export default AgreementErrorsWrapper;
